import { FirebaseError } from "firebase/app";
import backendConfig from "../config/backendConfig.ts";
import { CategoryType } from "../models/category-type.ts";
import { Product, ProductImages } from "../models/product.ts";
import { incrementCategoryQuantity } from "./category.service.ts";
import { imagesService } from "./images.service.ts";

// Helper function to make API requests
const apiRequest = async (endpoint: string, options: RequestInit = {}) => {
  const url = `${backendConfig.API_BASE_URL}${endpoint}`;

  const response = await fetch(url, {
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
    ...options,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error || `HTTP error! status: ${response.status}`,
    );
  }

  return response.json();
};

export const getProducts = async (): Promise<{
  success: boolean;
  data?: Product[];
  error?: FirebaseError;
}> => {
  try {
    const result = await apiRequest(backendConfig.ENDPOINTS.PRODUCTS);

    if (!result.success) {
      return {
        success: false,
        error: new Error(result.error) as FirebaseError,
      };
    }

    const products: Product[] = result.data.map((product: any) => ({
      id: product._id,
      name: product.name,
      description: product.description,
      price: product.price,
      category: product.category,
      season: product.season,
      stock: product.stock,
      type: product.type,
      sale: product.sale,
      fabric: product.fabric,
      design: product.design,
      stockOptions: product.stockOptions,
      images: product.images,
      createdAt: new Date(product.createdAt),
      updatedAt: new Date(product.updatedAt),
      isOutOfStock: product.isOutOfStock,
      sizeChart: product.sizeChart || "",
      ratingAverage: Number(product.ratingAverage) || 0,
      ratingCount: Number(product.ratingCount) || 0,
    }));

    return { success: true, data: products };
  } catch (error: any) {
    console.error("Fetching products failed:", error);
    return { success: false, error: error as FirebaseError };
  }
};

export const createProduct = async (
  product: Product,
  productImages: ProductImages,
): Promise<{ success: boolean; id?: string; error?: FirebaseError }> => {
  const {
    name,
    description,
    price,
    category,
    season = "",
    stock,
    type,
    sale,
    fabric,
    design,
    stockOptions = [],
    images = { front: "", back: "", detail: [] },
  } = product;

  try {
    const imagesResponse =
      await imagesService.multipleImageUploads(productImages);

    const productData = {
      name,
      description,
      price,
      category: category.id || category, // Handle both string ID and object
      season,
      type,
      stock,
      stockOptions,
      design,
      fabric,
      sale,
      images: imagesResponse,
      isOutOfStock: product.isOutOfStock || false,
      sizeChart: product.sizeChart || "",
    };

    const result = await apiRequest(backendConfig.ENDPOINTS.PRODUCTS, {
      method: "POST",
      body: JSON.stringify(productData),
    });

    if (!result.success) {
      return {
        success: false,
        error: new Error(result.error) as FirebaseError,
      };
    }

    return { success: true, id: result.id };
  } catch (error: any) {
    console.error("Product creation failed:", error.message, error.code);
    return { success: false, error: error as FirebaseError };
  }
};

export const editProduct = async (
  productId: string,
  updatedProduct: Partial<Product>,
): Promise<{ success: boolean; error?: unknown }> => {
  try {
    // Prevent undefined values
    const cleanedProduct = Object.fromEntries(
      Object.entries(updatedProduct).filter(
        ([_, value]) => value !== undefined,
      ),
    );

    // Get the old product to calculate quantity changes
    const oldProductResult = await apiRequest(
      `${backendConfig.ENDPOINTS.PRODUCTS}/${productId}`,
    );
    if (!oldProductResult.success) {
      return { success: false, error: "Product not found" };
    }

    const oldProduct = oldProductResult.data;
    const oldStock = oldProduct.stock;
    const oldCategoryId = oldProduct.category._id || oldProduct.category;

    const updatePayload = {
      ...cleanedProduct,
      category:
        typeof cleanedProduct.category === "object" &&
        cleanedProduct.category !== null
          ? (cleanedProduct.category as any).id || cleanedProduct.category
          : cleanedProduct.category,
    };

    const result = await apiRequest(
      `${backendConfig.ENDPOINTS.PRODUCTS}/${productId}`,
      {
        method: "PUT",
        body: JSON.stringify(updatePayload),
      },
    );

    if (!result.success) {
      return { success: false, error: result.error };
    }

    const newStock = updatedProduct.stock ?? 0;
    const newCategoryId =
      typeof updatedProduct.category === "object" &&
      updatedProduct.category !== null
        ? (updatedProduct.category as any).id || updatedProduct.category
        : updatedProduct.category;

    if (newCategoryId === oldCategoryId) {
      const delta = newStock - oldStock;
      await incrementCategoryQuantity(newCategoryId as string, delta);
    } else {
      await incrementCategoryQuantity(oldCategoryId as string, -oldStock);
      await incrementCategoryQuantity(newCategoryId as string, newStock);
    }

    return { success: true };
  } catch (error) {
    console.error("Product update failed:", error);
    return { success: false, error };
  }
};

export const deleteProduct = async (
  productId: string,
): Promise<{ success: boolean; error?: unknown }> => {
  try {
    // Get the product to get category and stock info
    const productResult = await apiRequest(
      `${backendConfig.ENDPOINTS.PRODUCTS}/${productId}`,
    );
    if (!productResult.success) {
      return { success: false, error: "Product not found" };
    }

    const product = productResult.data;
    await incrementCategoryQuantity(
      product.category._id || product.category,
      -product.stock,
    );

    const result = await apiRequest(
      `${backendConfig.ENDPOINTS.PRODUCTS}/${productId}`,
      {
        method: "DELETE",
      },
    );

    if (!result.success) {
      return { success: false, error: result.error };
    }

    return { success: true };
  } catch (error) {
    console.error("Product deletion failed:", error);
    return { success: false, error };
  }
};

export const getProductById = async (
  productId: string,
): Promise<{ success: boolean; data?: Product; error?: unknown }> => {
  try {
    const result = await apiRequest(
      `${backendConfig.ENDPOINTS.PRODUCTS}/${productId}`,
    );

    if (!result.success) {
      return { success: false, error: result.error || "Product not found" };
    }

    const product = result.data;
    return {
      success: true,
      data: {
        id: product._id,
        name: product.name,
        description: product.description,
        price: product.price,
        category: product.category,
        season: product.season,
        stock: product.stock,
        type: product.type,
        sale: product.sale,
        fabric: product.fabric,
        design: product.design,
        stockOptions: product.stockOptions,
        images: product.images,
        createdAt: new Date(product.createdAt),
        updatedAt: new Date(product.updatedAt),
        isOutOfStock: product.isOutOfStock,
        sizeChart: product.sizeChart || "",
        ratingAverage: Number(product.ratingAverage) || 0,
        ratingCount: Number(product.ratingCount) || 0,
      },
    };
  } catch (error) {
    console.error("Fetching product by ID failed:", error);
    return { success: false, error };
  }
};

export const getProductsByCategory = async (
  categoryType: CategoryType,
): Promise<{ success: boolean; data?: Product[]; error?: FirebaseError }> => {
  try {
    const result = await apiRequest(
      `${backendConfig.ENDPOINTS.PRODUCTS}/category/${categoryType}`,
    );

    if (!result.success) {
      return {
        success: false,
        error: new Error(result.error) as FirebaseError,
      };
    }

    const products: Product[] = result.data.map((product: any) => ({
      id: product._id,
      name: product.name,
      description: product.description,
      price: product.price,
      category: product.category,
      season: product.season,
      stock: product.stock,
      type: product.type,
      sale: product.sale,
      fabric: product.fabric,
      design: product.design,
      stockOptions: product.stockOptions,
      images: product.images,
      createdAt: new Date(product.createdAt),
      updatedAt: new Date(product.updatedAt),
      isOutOfStock: product.isOutOfStock,
      sizeChart: product.sizeChart || "",
      ratingAverage: Number(product.ratingAverage) || 0,
      ratingCount: Number(product.ratingCount) || 0,
    }));

    return { success: true, data: products };
  } catch (error: any) {
    console.error("Fetching products by category failed:", error);
    return { success: false, error: error as FirebaseError };
  }
};

export const getProductsColors = async (): Promise<{
  success: boolean;
  data?: String[];
  error?: FirebaseError;
}> => {
  try {
    const result = await apiRequest(
      `${backendConfig.ENDPOINTS.PRODUCTS}/aggregations/colors`,
    );

    if (!result.success) {
      return {
        success: false,
        error: new Error(result.error) as FirebaseError,
      };
    }

    return { success: true, data: result.data };
  } catch (error: any) {
    console.error("Fetching products by color failed:", error);
    return { success: false, error: error as FirebaseError };
  }
};

export const getProductsPrices = async (): Promise<{
  success: boolean;
  data?: { min: number; max: number };
  error?: FirebaseError;
}> => {
  try {
    const result = await apiRequest(
      `${backendConfig.ENDPOINTS.PRODUCTS}/aggregations/prices`,
    );

    if (!result.success) {
      return {
        success: false,
        error: new Error(result.error) as FirebaseError,
      };
    }

    return { success: true, data: result.data };
  } catch (error: any) {
    console.error("Fetching products prices failed:", error);
    return { success: false, error: error as FirebaseError };
  }
};

export const getAllProductFabrics = async (): Promise<{
  success: boolean;
  data?: String[];
  error?: FirebaseError;
}> => {
  try {
    const result = await apiRequest(
      `${backendConfig.ENDPOINTS.PRODUCTS}/aggregations/fabrics`,
    );

    if (!result.success) {
      return {
        success: false,
        error: new Error(result.error) as FirebaseError,
      };
    }

    return { success: true, data: result.data };
  } catch (error: any) {
    console.error("Fetching all product fabrics failed:", error);
    return { success: false, error: error as FirebaseError };
  }
};
